import { Component, type ErrorInfo, type ReactNode } from "react"
import { AppIcon } from "@/components/app_icon"
import { t } from "@/lib/i18n"

type AppErrorBoundaryProps = {
  children: ReactNode
}

type AppErrorBoundaryState = {
  error: Error | null
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Metimely crashed.", error, errorInfo)
    void import("@/lib/posthog").then(({ reportAppError }) => {
      reportAppError(error, {
        source: "react_error_boundary",
        component_stack: errorInfo.componentStack,
        handled: true,
      })
    })
  }

  reload() {
    window.location.reload()
  }

  render() {
    if (!this.state.error) {
      return this.props.children
    }

    return (
      <main className="grid min-h-screen bg-background px-4 py-8 text-foreground sm:px-6">
        <div className="mx-auto flex w-full max-w-xl flex-col justify-center">
          <div className="mb-8 inline-flex items-center gap-2 font-semibold">
            <AppIcon className="h-10 w-10" />
            <span>Metimely</span>
          </div>

          <section className="rounded-lg border bg-card p-6 shadow-sm sm:p-8" role="alert" aria-live="assertive">
            <p className="text-sm font-medium uppercase text-muted-foreground">{t("frontend.application.error_eyebrow")}</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-normal">{t("frontend.application.error_title")}</h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{t("frontend.application.error_description")}</p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                type="button"
                onClick={this.reload}
              >
                {t("frontend.application.error_reload")}
              </button>
              <a
                className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                href="/dashboard"
              >
                {t("frontend.application.error_dashboard")}
              </a>
            </div>

            {import.meta.env.DEV && (
              <pre className="mt-6 max-h-48 overflow-auto rounded-md border bg-muted p-3 text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
          </section>
        </div>
      </main>
    )
  }
}
